import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Header } from './Header';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { NamePrompt } from './NamePrompt';
import { TypingIndicator } from './TypingIndicator';
import { LeaveConfirm } from './LeaveConfirm';
import { LeftScreen } from './LeftScreen';
import { DisconnectedScreen } from './DisconnectedScreen';
import { LockedState } from './LockedState';
import { ActivationModal } from './ActivationModal';
import { BlockedToast } from './BlockedToast';
import { BannedScreen } from './BannedScreen';
import { ChatIntro } from './ChatIntro';
import { ReplyBanner } from './ReplyBanner';
import { ChatClient } from '../socket/socket-client';
import { useChatStore } from '../store/chat-store';

export interface VipChatProps {
  /** Base URL of the chat backend, e.g. http://localhost:4000 */
  serverUrl: string;
  /**
   * JWT minted by the host application. When provided, authenticated mode
   * is used and the name prompt is skipped.
   */
  token?: string;
  /**
   * Optional callback to fetch a fresh token when the current one expires.
   * Recommended in production so a 15-min token rotates seamlessly.
   */
  tokenProvider?: () => Promise<string>;
  /** Optional fixed height; defaults to 100% of container. */
  height?: string | number;
  /** localStorage key for the persisted anonymous session. */
  storageKey?: string;
  /** Optional welcome card shown above an empty message list. */
  introTitle?: string;
  introMessage?: string;
  /** Quick-reply chips fired as normal chat messages when clicked. */
  quickReplies?: string[];
  /** Brand name shown in an optional footer band. */
  brandName?: string;
}

interface AnonSession {
  name: string;
  clientSessionId: string;
}

function loadAnon(key: string): AnonSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AnonSession;
    if (parsed?.name && parsed?.clientSessionId) return parsed;
  } catch {/* ignore */}
  return null;
}
function saveAnon(key: string, value: AnonSession): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
}
function clearAnon(key: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(key);
}

export function VipChat({
  serverUrl,
  token,
  tokenProvider,
  height = '100%',
  storageKey = 'vip-chat-session',
  introTitle,
  introMessage,
  quickReplies,
  brandName,
}: VipChatProps) {
  const clientRef = useRef<ChatClient | null>(null);
  const identity = useChatStore(s => s.identity);
  const [askLeave, setAskLeave] = useState(false);
  // True after the user leaves in JWT mode: we show a "log in to rejoin"
  // screen instead of a dead, disconnected chat shell.
  const [left, setLeft] = useState(false);
  // Remember the last non-empty display name so the leave dialog / left
  // screen can show it even after disconnect clears `identity`.
  const lastNameRef = useRef('');
  // Bumped to force a fresh connection attempt from the Reconnect button.
  const [reconnectNonce, setReconnectNonce] = useState(0);

  // JWT mode? When a token (or token provider) is given, authenticated path.
  const jwtMode = Boolean(token || tokenProvider);

  // Anonymous fallback only used when no token/callback provided.
  const [anonSession, setAnonSession] = useState<AnonSession | null>(
    () => (jwtMode ? null : loadAnon(storageKey)),
  );

  const userId = identity?.userId ?? anonSession?.clientSessionId ?? '';
  const userName = identity?.name ?? anonSession?.name ?? '';
  if (userName) lastNameRef.current = userName;

  const handleSubmitName = (name: string) => {
    const next: AnonSession = {
      name,
      clientSessionId: anonSession?.clientSessionId ?? uuid(),
    };
    saveAnon(storageKey, next);
    setAnonSession(next);
  };

  const requestLeave = useCallback(() => setAskLeave(true), []);
  const cancelLeave = useCallback(() => setAskLeave(false), []);
  const confirmLeave = useCallback(() => {
    setAskLeave(false);
    clientRef.current?.disconnect();
    useChatStore.getState().reset();
    if (jwtMode) {
      // Authenticated mode: show the "log in to rejoin" screen instead of a
      // dead chat shell. Login itself is the host app's responsibility.
      setLeft(true);
    } else {
      clearAnon(storageKey);
      setAnonSession(null);
    }
  }, [jwtMode, storageKey]);

  const handleRejoin = useCallback(() => {
    useChatStore.getState().reset();
    setLeft(false); // re-runs the connect effect → reconnects with the token
  }, []);

  const handleReconnect = useCallback(() => {
    useChatStore.getState().setConn('connecting');
    setReconnectNonce(n => n + 1); // re-runs the connect effect → fresh socket
  }, []);

  // Read locked state separately so we can re-run the connect effect once
  // the eligibility poll discovers the user is now VIP.
  const locked = useChatStore(s => s.eligibility.locked);
  const ban = useChatStore(s => s.ban);
  const connState = useChatStore(s => s.connState);
  const errorMessage = useChatStore(s => s.errorMessage);
  const isHardBan = ban && ban.type !== 'mute';
  // Intro is only useful while the conversation is empty.
  const messageCount = useChatStore(s => s.messages.length);
  const showIntro = messageCount === 0;

  const sendQuickReply = useCallback(
    (text: string) => clientRef.current?.send(text, userId, userName),
    [userId, userName],
  );

  useEffect(() => {
    // While the widget is locked, or the user has explicitly left, don't
    // try to connect a socket.
    if (locked || left) return;
    // JWT mode connects as soon as a token is available.
    if (jwtMode && token) {
      const c = new ChatClient();
      clientRef.current = c;
      c.connect({ mode: 'jwt', serverUrl, token, tokenProvider });
      return () => c.disconnect();
    }
    // Anonymous mode connects after the user submits a name.
    if (!jwtMode && anonSession) {
      const c = new ChatClient();
      clientRef.current = c;
      c.connect({ mode: 'anonymous', serverUrl, name: anonSession.name, clientSessionId: anonSession.clientSessionId });
      return () => c.disconnect();
    }
  }, [locked, left, reconnectNonce, jwtMode, token, tokenProvider, serverUrl, anonSession]);

  const containerStyle = useMemo(() => ({ height, width: '100%' }), [height]);

  // Authenticated user left: show the "log in to rejoin" screen instead of
  // a disconnected chat shell.
  if (jwtMode && left) {
    return (
      <div className="vip-chat-root flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" style={containerStyle}>
        <LeftScreen name={lastNameRef.current} onRejoin={handleRejoin} />
      </div>
    );
  }

  // JWT mode without a token yet: show a quiet placeholder.
  if (jwtMode && !token) {
    return (
      <div className="vip-chat-root flex flex-col items-center justify-center bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 text-center text-sm text-gray-500" style={containerStyle}>
        Waiting for authentication token…
      </div>
    );
  }

  // Anonymous mode + no name yet: show name prompt.
  if (!jwtMode && !anonSession) {
    return (
      <div className="vip-chat-root flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" style={containerStyle}>
        <NamePrompt onSubmit={handleSubmitName} />
      </div>
    );
  }

  // Permanent / temporary ban: replace the whole chat surface with a notice.
  if (isHardBan) {
    return (
      <div className="vip-chat-root flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" style={containerStyle}>
        <BannedScreen />
      </div>
    );
  }

  // Server rejected with NOT_VIP: show locked screen with progress + auto-poll.
  if (locked && jwtMode && token) {
    return (
      <div className="vip-chat-root flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" style={containerStyle}>
        <LockedState
          serverUrl={serverUrl}
          token={token}
          onUnlocked={() => useChatStore.getState().setEligibility(false, null)}
        />
      </div>
    );
  }

  // Stuck disconnected (e.g. server dropped the socket — Socket.IO won't
  // auto-reconnect): show a dedicated screen with a Reconnect button instead
  // of a dead composer.
  if (connState === 'error') {
    return (
      <div className="vip-chat-root flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5" style={containerStyle}>
        <DisconnectedScreen message={errorMessage} onReconnect={handleReconnect} />
      </div>
    );
  }

  return (
    <div
      className="vip-chat-root relative flex flex-col bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5"
      style={containerStyle}
    >
      <Header onLeave={requestLeave} />
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {showIntro && (
          <ChatIntro
            title={introTitle}
            message={introMessage}
            quickReplies={quickReplies}
            onQuickReply={sendQuickReply}
          />
        )}
        <MessageList
          currentUserId={userId}
          onReact={(id, emoji) => clientRef.current?.react(id, emoji)}
          serverUrl={serverUrl}
        />
      </div>
      <TypingIndicator currentUserId={userId} />
      <ReplyBanner />
      <Composer
        onSend={text => clientRef.current?.send(text, userId, userName)}
        onTypingStart={() => clientRef.current?.sendTypingStart()}
        onTypingStop={() => clientRef.current?.sendTypingStop()}
        onSendVoice={
          jwtMode
            ? (blob, durationMs) => clientRef.current?.sendVoice(blob, durationMs, userId, userName)
            : undefined
        }
      />
      {brandName && (
        <div className="flex items-center justify-center gap-1.5 bg-gray-50 border-t border-gray-100 py-1.5 text-[10.5px] text-gray-400 select-none">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Powered by <strong className="text-gray-500 font-semibold">{brandName}</strong>
        </div>
      )}
      {askLeave && <LeaveConfirm name={userName || lastNameRef.current} onCancel={cancelLeave} onConfirm={confirmLeave} />}
      <ActivationModal client={clientRef.current} />
      <BlockedToast />
    </div>
  );
}
